/**
 * pixi-postfx.js - Premium PixiJS Post-Processing Effects
 * Ajoute bloom subtil + vignette douce au board Pixi
 * S'adapte automatiquement aux performances
 */

(function() {
  'use strict';
  
  // Attendre que PixiJS et le board soient initialisés
  function waitForPixi(callback, maxAttempts = 100) {
    let attempts = 0;
    
    const check = () => {
      attempts++;
      
      // Vérifier que PIXI est chargé ET que pixiBoard a créé l'app
      if (window.PIXI && window.__pixiApp && window.__pixiApp.stage) {
        console.log('[PixiPostFX] PIXI and app ready, initializing...');
        callback();
      } else if (attempts < maxAttempts) {
        setTimeout(check, 200); // Attendre plus longtemps entre chaque tentative
      } else {
        console.warn('[PixiPostFX] Could not initialize after', attempts, 'attempts');
        console.warn('[PixiPostFX] PIXI:', !!window.PIXI, '__pixiApp:', !!window.__pixiApp);
      }
    };
    
    check();
  }
  
  class PixiPostFX {
    constructor(app) {
      this.app = app;
      this.enabled = true;
      // Bloom via BlurFilter peut donner un rendu "mou" (surtout sur les petites cartes / terrain adverse).
      // On le désactive par défaut. La vignette seule donne déjà un look premium sans perdre en netteté.
      this.bloomEnabled = false;
      this.vignetteEnabled = true;
      this.filters = [];
      
      // Configuration des effets
      this.config = {
        bloom: {
          strength: 0.0,
          blur: 0,
          quality: 4,
          threshold: 0.5
        },
        vignette: {
          strength: 0.3,       // Doux
          radius: 0.8
        }
      };
    }
    
    init() {
      try {
        if (!this.app || !this.app.stage) {
          console.warn('[PixiPostFX] App or stage not ready');
          return;
        }
        
        console.log('[PixiPostFX] Initializing effects...');

        // IMPORTANT (PixiJS v8.x):
        // - Le bundle CDN "pixi.min.js" ne contient pas toujours les filtres avancés.
        // - Les API de shaders/Program ont changé (GlProgram/Filter.from).
        // Pour éviter les erreurs et rester ultra-compatible, on applique:
        // 1) Un "bloom" très léger si BlurFilter est dispo (sinon skip)
        // 2) Une vignette via overlay (CanvasTexture) plutôt qu'un shader.
        this.createEffects();

        // Vérifier les performances et adapter
        this.monitorPerformance();
        
        console.log('[PixiPostFX] Effects initialized');
      } catch (error) {
        console.error('[PixiPostFX] Init error:', error);
        this.enabled = false;
      }
    }

    // ---- Effects creation (compat Pixi v7/v8 CDN) ----
    createEffects() {
      // 1) Bloom léger (optionnel)
      if (this.bloomEnabled) {
        this.bloomFilter = this.createBloomFilter();
        if (this.bloomFilter && this.app.stage) {
          // Appliquer au stage uniquement si dispo
          this.app.stage.filters = (this.app.stage.filters || []).concat([this.bloomFilter]);
        }
      }

      // 2) Vignette overlay (robuste)
      if (this.vignetteEnabled) {
        this.createVignetteOverlay();
      }
    }
    
    createBloomFilter() {
      try {
        // Utiliser un BlurFilter simple comme "glow" (si dispo)
        // NB: Sur PixiJS v8 CDN, BlurFilter peut exister, mais BLEND_MODES peut varier.
        const BlurFilter = (PIXI.filters && PIXI.filters.BlurFilter) || PIXI.BlurFilter;
        if (!BlurFilter) return null;

        const blur = new BlurFilter({
          strength: this.config.bloom.blur,
          quality: this.config.bloom.quality,
        });

        // Essayer un blend additif si l'enum est accessible, sinon on laisse en NORMAL.
        const BLEND = PIXI.BLEND_MODES || PIXI.BLEND_MODE || PIXI.blendModes;
        const ADD = BLEND && (BLEND.ADD ?? BLEND.ADDITIVE ?? BLEND.Add);
        if (typeof ADD === 'number') {
          blur.blendMode = ADD;
        }

        // alpha est supporté sur Filter / BlurFilter
        blur.alpha = this.config.bloom.strength;
        return blur;
      } catch (error) {
        console.warn('[PixiPostFX] Could not create bloom filter:', error);
        return null;
      }
    }

    createVignetteOverlay() {
      try {
        const Texture = PIXI.Texture;
        const Sprite = PIXI.Sprite;
        const Container = PIXI.Container;
        if (!Texture || !Sprite || !Container) {
          console.warn('[PixiPostFX] Vignette overlay skipped: PIXI core classes missing');
          return;
        }

        // Créer une texture de vignette via canvas (512x512)
        const size = 512;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const g = ctx.createRadialGradient(size / 2, size / 2, size * 0.15, size / 2, size / 2, size * 0.5);
        // centre transparent
        g.addColorStop(0, 'rgba(0,0,0,0)');
        g.addColorStop(0.6, 'rgba(0,0,0,0.0)');
        // bords assombris (subtil)
        const edgeAlpha = Math.max(0, Math.min(0.75, this.config.vignette.strength));
        g.addColorStop(1, `rgba(0,0,0,${edgeAlpha})`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, size, size);

        const tex = Texture.from(canvas);
        const spr = new Sprite(tex);
        spr.anchor.set(0.5);

        // Blend mode: MULTIPLY si dispo, sinon NORMAL.
        const BLEND = PIXI.BLEND_MODES || PIXI.BLEND_MODE || PIXI.blendModes;
        const MULTIPLY = BLEND && (BLEND.MULTIPLY ?? BLEND.Multiply);
        if (typeof MULTIPLY === 'number') spr.blendMode = MULTIPLY;

        // Overlay container au-dessus de tout
        this.vignetteOverlay = new Container();
        this.vignetteOverlay.eventMode = 'none';
        this.vignetteOverlay.sortableChildren = false;
        this.vignetteOverlay.addChild(spr);
        this.vignetteSprite = spr;

        // Toujours en dernier dans le stage
        this.app.stage.addChild(this.vignetteOverlay);

        // Taille / position
        this.updateOverlayLayout();

        // Réagir aux resize
        window.addEventListener('resize', () => this.updateOverlayLayout(), { passive: true });
      } catch (error) {
        console.warn('[PixiPostFX] Vignette overlay failed:', error);
      }
    }

    updateOverlayLayout() {
      if (!this.vignetteSprite || !this.app) return;
      const w = this.app.screen ? this.app.screen.width : (this.app.renderer && this.app.renderer.width) || window.innerWidth;
      const h = this.app.screen ? this.app.screen.height : (this.app.renderer && this.app.renderer.height) || window.innerHeight;
      const s = Math.max(w, h) * 1.25;
      this.vignetteSprite.position.set(w / 2, h / 2);
      this.vignetteSprite.width = s;
      this.vignetteSprite.height = s;
    }
    
    setBloomStrength(strength) {
      this.config.bloom.strength = Math.max(0, Math.min(1, strength));
      
      if (this.bloomFilter) {
        this.bloomFilter.alpha = this.config.bloom.strength;
      }
    }
    
    setVignetteStrength(strength) {
      this.config.vignette.strength = Math.max(0, Math.min(1, strength));

      // Overlay version: on reconstruit simplement la texture au prochain enable,
      // mais ici on ajuste l'alpha pour un feedback immédiat.
      if (this.vignetteOverlay) {
        // alpha douce, mais lisible en plein soleil
        this.vignetteOverlay.alpha = 0.35 + (this.config.vignette.strength * 0.45);
      }
    }

    // Compat API (anciens appels) — désormais on gère un bloom (stage.filters) + un overlay (sprite)
    applyFilters() {
      // Bloom
      if (this.app && this.app.stage && this.bloomFilter) {
        const current = this.app.stage.filters || [];
        if (!current.includes(this.bloomFilter)) {
          this.app.stage.filters = current.concat([this.bloomFilter]);
        }
      }
      // Vignette overlay
      if (this.vignetteOverlay) this.vignetteOverlay.visible = this.enabled && this.vignetteEnabled;
    }

    removeFilters() {
      // Bloom
      if (this.app && this.app.stage && this.bloomFilter) {
        const current = this.app.stage.filters || [];
        this.app.stage.filters = current.filter(f => f !== this.bloomFilter);
      }
      // Vignette overlay
      if (this.vignetteOverlay) this.vignetteOverlay.visible = false;
    }
    
    enable() {
      this.enabled = true;
      this.applyFilters();
    }
    
    disable() {
      this.enabled = false;
      this.removeFilters();
    }
    
    monitorPerformance() {
      // Vérifier si le mode low-perf est activé
      const checkPerf = () => {
        if (document.body.classList.contains('low-perf')) {
          this.reduceEffects();
        }
      };
      
      // Vérifier toutes les 5 secondes
      setInterval(checkPerf, 5000);
      
      // Observer les changements de classe
      if (window.MutationObserver) {
        const observer = new MutationObserver(checkPerf);
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    }
    
    reduceEffects() {
      console.log('[PixiPostFX] Reducing effects for performance');
      
      // Réduire l'intensité du bloom
      this.setBloomStrength(this.config.bloom.strength * 0.5);
      
      // Désactiver la vignette si vraiment nécessaire
      if (this.vignetteOverlay) {
        this.vignetteEnabled = false;
        this.applyFilters();
      }
    }
    
    restoreEffects() {
      console.log('[PixiPostFX] Restoring effects');
      
      // Restaurer le bloom
      this.setBloomStrength(this.config.bloom.strength);
      
      // Réactiver la vignette
      if (!this.vignetteEnabled && this.vignetteOverlay) {
        this.vignetteEnabled = true;
        this.applyFilters();
      }
    }
  }
  
  // Initialiser les PostFX quand Pixi est prêt
  waitForPixi(() => {
    try {
      const postfx = new PixiPostFX(window.__pixiApp);
      postfx.init();
      
      // Exposer globalement
      window.__pixiPostFX = postfx;
      
      // Intégration avec le système premium
      if (window.premiumUI) {
        const originalReduce = window.premiumUI.reduceEffects.bind(window.premiumUI);
        const originalRestore = window.premiumUI.restoreEffects.bind(window.premiumUI);
        
        window.premiumUI.reduceEffects = function() {
          originalReduce();
          if (window.__pixiPostFX) {
            window.__pixiPostFX.reduceEffects();
          }
        };
        
        window.premiumUI.restoreEffects = function() {
          originalRestore();
          if (window.__pixiPostFX) {
            window.__pixiPostFX.restoreEffects();
          }
        };
      }
      
      console.log('[PixiPostFX] Ready');
    } catch (error) {
      console.error('[PixiPostFX] Initialization failed:', error);
    }
  });
  
})();
